import { useEffect, useRef, useState } from 'react';
import { Bot, Send, X, Minimize2, Sparkles, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { askSocialOpsAssistant } from '../api/assistant';

interface Message {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  facts?: {
    accounts: number;
    healthyAccounts: number;
    pendingReviews: number;
    failedJobs: number;
    queuedJobs: number;
    scheduledPosts: number;
  };
}

const starterQuestions = [
  '账号整体情况怎么样？',
  '哪些平台现在可以发布？',
  '有没有失败的发布任务？',
  '现在有哪些内容待审核？',
  '最近账号和发布有什么异常？',
];

export function SocialOpsAssistant({ workspaceId, workspace }: { workspaceId?: string; workspace?: string }) {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: '我是 Loopora 社媒运营助手。可以帮你查看当前 workspace 的账号健康、平台发布能力、发布队列、审核任务、排期内容、Campaign 和审计日志。',
    },
  ]);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  async function send(text = input) {
    const message = text.trim();
    if (!message || loading) return;
    if (!workspaceId) {
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: '请先选择一个 workspace，我才能读取对应账号和运营数据。' }]);
      return;
    }
    setInput('');
    setLoading(true);
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', content: message }]);
    try {
      const res = await askSocialOpsAssistant(workspaceId, message);
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: res.answer,
          facts: res.quickFacts,
        },
      ]);
    } catch (err: any) {
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: err?.message || '助手读取 workspace 数据失败，请稍后重试。' }]);
    } finally {
      setLoading(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-2xl bg-blue-600 px-4 py-3 text-white shadow-xl shadow-blue-900/20 hover:bg-blue-700 transition"
      >
        <span className="relative flex size-9 items-center justify-center rounded-xl bg-white/15">
          <Bot size={20} />
          <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-emerald-400 ring-2 ring-blue-600" />
        </span>
        <span className="hidden sm:block text-left">
          <span className="block text-xs font-extrabold leading-tight">社媒运营助手</span>
          <span className="block text-[10px] text-blue-100 font-semibold">问账号 / 发布 / 审核</span>
        </span>
      </button>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl shadow-gray-900/15 ${minimized ? 'h-16' : 'h-[620px] max-h-[calc(100vh-3rem)]'}`}>
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-950 px-4 py-3 text-white">
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-xl bg-blue-600">
            <Sparkles size={18} />
          </div>
          <div>
            <p className="text-sm font-extrabold leading-tight">Loopora 社媒运营助手</p>
            <p className="text-[10px] text-gray-300 font-semibold truncate max-w-[220px]">{workspace || 'No workspace selected'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized(!minimized)} className="rounded-lg p-2 text-gray-300 hover:bg-white/10 hover:text-white"><Minimize2 size={14} /></button>
          <button onClick={() => setOpen(false)} className="rounded-lg p-2 text-gray-300 hover:bg-white/10 hover:text-white"><X size={14} /></button>
        </div>
      </div>

      {!minimized && (
        <>
          <div className="h-[430px] overflow-y-auto bg-gray-50/60 px-4 py-4 space-y-3">
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[86%] rounded-2xl px-3.5 py-3 text-xs leading-relaxed whitespace-pre-line ${message.role === 'user' ? 'bg-blue-600 text-white rounded-br-md' : 'bg-white text-gray-750 border border-gray-200 rounded-bl-md shadow-xs'}`}>
                  {message.content}
                  {message.facts && (
                    <div className="mt-3 grid grid-cols-2 gap-1.5 border-t border-gray-100 pt-2 text-[10px]">
                      <span className="flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-500" />账号 {message.facts.accounts}</span>
                      <span>健康 {message.facts.healthyAccounts}</span>
                      <span>待审 {message.facts.pendingReviews}</span>
                      <span className={message.facts.failedJobs > 0 ? 'text-red-600 font-bold' : ''}>失败 {message.facts.failedJobs}</span>
                      <span>队列 {message.facts.queuedJobs}</span>
                      <span>排期 {message.facts.scheduledPosts}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-md border border-gray-200 bg-white px-3.5 py-3 text-xs text-gray-500 shadow-xs">
                  <RefreshCw size={13} className="animate-spin" /> 正在读取账号和运营数据...
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-gray-100 bg-white p-3">
            <div className="mb-2 flex gap-1.5 overflow-x-auto pb-1">
              {starterQuestions.map((question) => (
                <button key={question} onClick={() => send(question)} disabled={loading} className="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[10px] font-bold text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-50">
                  {question}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) send();
                }}
                placeholder="问：哪个账号异常？哪些平台能发？"
                className="min-w-0 flex-1 bg-transparent text-xs font-semibold text-gray-800 outline-none placeholder:text-gray-400"
              />
              <button onClick={() => send()} disabled={loading || !input.trim()} className="flex size-8 items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
                <Send size={14} />
              </button>
            </div>
            <p className="mt-2 flex items-center gap-1 text-[9px] text-gray-400">
              <AlertTriangle size={10} /> 只读取当前 workspace 数据；不会直接替你发布或删除内容。
            </p>
          </div>
        </>
      )}
    </div>
  );
}
